import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import SelectField from "../common/SelectField.js";

import { useNavigate, useParams } from "react-router-dom";

import Alert from "../common/Alert.js";

import { DashboardLayout } from "../common/page_component/index.js";

import Loader from "../common/Loader.js";

import ListPageHeader from "../common/ListPageHeader.js";

import shipmentClientService from "../../services/ShipmentClientService.js";


import { Download, FileText, IndianRupee, X } from "lucide-react";

import {
  validators,
  inputFilters,
  useValidation,
  ValidationMessage,
} from "../common/Validation.js";

import Button from "../common/Button.js";

import { useTranslation } from "react-i18next";

import { fullCurrency } from "../../utils/formatCurrency.js";

import { formatIndianDate } from "../../utils/localDate.js";

import {
  emptyStateClass,
  listIdLinkClass,
  listIdLinkMobileClass,
  listPageWrapperClass,
  mobileCardClass,
  tableBodyClass,
  tableCellClass,
  tableClass,
  tableHeadCellClass,
  tableHeadRowClass,
  tableRowClass,
} from "../common/uiClasses.js";

const compactControlClass =
  "w-full min-w-0 h-10 px-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm bg-white";

const tableSearchInputClass =
  "w-full max-w-[11rem] sm:max-w-[14rem] h-7 px-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 text-[11px] bg-white placeholder:text-gray-400";

const StatTile = ({ label, value, tone = "gray" }) => {
  const tones = {
    blue: "bg-blue-50 text-blue-700",

    green: "bg-green-50 text-green-700",

    red: "bg-red-50 text-red-700",

    purple: "bg-purple-50 text-purple-700",

    gray: "bg-gray-50 text-gray-800",
  };

  return (
    <div
      className={`rounded-xl px-2.5 py-2 min-w-0 ${tones[tone] || tones.gray}`}
    >
      <p className="text-[10px] sm:text-[11px] font-medium opacity-80">
        {label}
      </p>

      <p className="text-sm sm:text-base font-bold tabular-nums truncate">
        {value}
      </p>
    </div>
  );
};

const computeSummaryFromShipments = (rows) => {
  const total_amount = rows.reduce(
    (sum, s) => sum + Number(s.total_amount || 0),
    0,
  );

  const paid_amount = rows.reduce(
    (sum, s) => sum + Number(s.paid_amount || 0),
    0,
  );

  const balance_amount = rows.reduce(
    (sum, s) => sum + Number(s.balance_amount || 0),
    0,
  );

  return {
    total_shipments: rows.length,

    total_amount: Math.round(total_amount * 100) / 100,

    paid_amount: Math.round(paid_amount * 100) / 100,

    balance_amount: Math.round(balance_amount * 100) / 100,
  };
};

const matchTableSearch = (shipment, rawQuery) => {
  const q = String(rawQuery || "").trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    shipment.shipment_id,
    shipment.consignment_no,
    shipment.sender_name,
    shipment.receiver_name,
    shipment.sender_location,
    shipment.receiver_location,
    shipment.status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

const VendorBatchShipmentBill = () => {
  const { t } = useTranslation();

  const { clientId } = useParams();

  const navigate = useNavigate();

  const [client, setClient] = useState(null);

  const [shipments, setShipments] = useState([]);

  const [summary, setSummary] = useState({
    total_shipments: 0,

    total_amount: 0,

    paid_amount: 0,

    balance_amount: 0,
  });

  /** All client shipments (no branch/search filters) — drives From/To branch dropdowns. */
  const [bookedShipmentsForRoutes, setBookedShipmentsForRoutes] = useState([]);

  const [initialLoading, setInitialLoading] = useState(true);

  const [listRefreshing, setListRefreshing] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const fetchSeqRef = useRef(0);

  const [submitting, setSubmitting] = useState(false);

  const [exporting, setExporting] = useState(false);

  const [downloading, setDownloading] = useState(false);

  const [fromDate, setFromDate] = useState("");

  const [toDate, setToDate] = useState("");

  const [fromBranchId, setFromBranchId] = useState("");

  const [toBranchId, setToBranchId] = useState("");

  const [tableSearch, setTableSearch] = useState("");

  const [selectedShipmentIds, setSelectedShipmentIds] = useState(
    () => new Set(),
  );

  const [paymentAmount, setPaymentAmount] = useState("");

  const [paymentMode, setPaymentMode] = useState("cash");

  const { errors, validate, validateField, clearField } = useValidation();

  const hasPageFilters = Boolean(
    fromDate || toDate || fromBranchId || toBranchId,
  );

  const hasTableSearch = Boolean(tableSearch.trim());

  const filterParams = useCallback(() => {
    const params = {};

    if (fromDate) params.from_date = fromDate;

    if (toDate) params.to_date = toDate;

    if (fromBranchId) params.sender_location_hub_id = fromBranchId;

    if (toBranchId) params.receiver_location_hub_id = toBranchId;

    if (tableSearch.trim()) params.q = tableSearch.trim();

    return params;
  }, [fromDate, toDate, fromBranchId, toBranchId, tableSearch]);

  const listShipments = useMemo(() => {
    const q = tableSearch.trim();
    if (!q) return shipments;
    return shipments.filter((s) => matchTableSearch(s, q));
  }, [shipments, tableSearch]);

  const listSummary = useMemo(() => {
    if (!tableSearch.trim()) return summary;
    return computeSummaryFromShipments(listShipments);
  }, [summary, listShipments, tableSearch]);

  const payableShipments = useMemo(
    () => listShipments.filter((s) => Number(s.balance_amount || 0) > 0),

    [listShipments],
  );

  const selectedPayable = useMemo(
    () =>
      payableShipments.filter((s) => selectedShipmentIds.has(s.shipment_id)),

    [payableShipments, selectedShipmentIds],
  );

  const selectedDueAmount = useMemo(
    () =>
      selectedPayable.reduce(
        (sum, s) => sum + Number(s.balance_amount || 0),
        0,
      ),

    [selectedPayable],
  );

  const maxPaymentAmount = useMemo(() => {
    const raw =
      selectedPayable.length > 0
        ? selectedDueAmount
        : Number(listSummary.balance_amount || 0);
    return Math.round(raw * 100) / 100;
  }, [selectedPayable.length, selectedDueAmount, listSummary.balance_amount]);

  const paymentAmountRule = useCallback(
    (v) => {
      const requiredErr = validators.required(v, "Payment amount");
      if (requiredErr) return requiredErr;

      const numberErr = validators.number(v, {
        min: 0.01,
        label: "Payment amount",
      });
      if (numberErr) return numberErr;

      if (selectedPayable.length === 0) {
        return "";
      }

      if (Number(v) > maxPaymentAmount + 0.009) {
        return `Payment amount cannot exceed due amount of ${fullCurrency(maxPaymentAmount)}`;
      }

      return "";
    },
    [maxPaymentAmount, selectedPayable.length],
  );

  const validationRules = useMemo(
    () => ({ paymentAmount: paymentAmountRule }),
    [paymentAmountRule],
  );

  const isPaymentReady = useMemo(() => {
    if (selectedPayable.length === 0 || !paymentAmount) return false;
    const amt = Number(paymentAmount);
    if (!amt || amt <= 0) return false;
    if (amt > maxPaymentAmount + 0.009) return false;
    return !paymentAmountRule(paymentAmount);
  }, [paymentAmount, maxPaymentAmount, selectedPayable.length, paymentAmountRule]);

  const mergeUpdatedShipments = useCallback((updatedDetails) => {
    if (!Array.isArray(updatedDetails) || updatedDetails.length === 0) return;

    const byId = Object.fromEntries(
      updatedDetails.map((row) => [row.shipment_id, row]),
    );

    setShipments((prev) => {
      const next = prev.map((row) => byId[row.shipment_id] || row);

      setSummary(computeSummaryFromShipments(next));

      return next;
    });

    setSelectedShipmentIds((prev) => {
      const next = new Set(prev);

      updatedDetails.forEach((row) => {
        if (Number(row.balance_amount || 0) <= 0) {
          next.delete(row.shipment_id);
        }
      });

      return next;
    });
  }, []);

  const fetchClientShipments = useCallback(
    async ({ silent = false } = {}) => {
      if (!clientId) return;

      const fetchSeq = ++fetchSeqRef.current;

      try {
        if (silent) {
          setListRefreshing(true);
        } else if (!hasLoadedOnceRef.current) {
          setInitialLoading(true);
        } else {
          setListRefreshing(true);
        }

        const response = await shipmentClientService.getClientShipments(
          clientId,
          filterParams(),
        );

        if (fetchSeq !== fetchSeqRef.current) return;

        if (response.success) {
          setClient(response.client);

          setShipments(response.shipments);

          setSummary(response.summary);

          setSelectedShipmentIds((prev) => {
            const visible = new Set(
              (response.shipments || []).map((s) => s.shipment_id),
            );

            const next = new Set();

            prev.forEach((id) => {
              if (visible.has(id)) next.add(id);
            });

            return next;
          });
        }
      } catch (err) {
        Alert.error("Failed to load client shipments");

        console.error(err);
      } finally {
        hasLoadedOnceRef.current = true;

        setInitialLoading(false);

        setListRefreshing(false);
      }
    },

    [clientId, filterParams],
  );

  const refreshBookedRouteShipments = useCallback(async () => {
    if (!clientId) return;
    try {
      const response = await shipmentClientService.getClientShipments(clientId, {});
      if (response.success) {
        setBookedShipmentsForRoutes(response.shipments || []);
      }
    } catch {
      setBookedShipmentsForRoutes([]);
    }
  }, [clientId]);

  useEffect(() => {
    refreshBookedRouteShipments();
  }, [refreshBookedRouteShipments]);

  useEffect(() => {
    const delay = hasLoadedOnceRef.current ? 220 : 0;

    const timer = setTimeout(() => {
      fetchClientShipments({ silent: hasLoadedOnceRef.current });
    }, delay);

    return () => clearTimeout(timer);
  }, [
    fetchClientShipments,
    fromDate,
    toDate,
    fromBranchId,
    toBranchId,
    tableSearch,
  ]);

  useEffect(() => {
    if (!paymentAmount) {
      clearField("paymentAmount");
      return;
    }
    validateField("paymentAmount", paymentAmount, paymentAmountRule);
  }, [
    paymentAmount,
    maxPaymentAmount,
    selectedPayable.length,
    selectedDueAmount,
    paymentAmountRule,
    validateField,
    clearField,
  ]);

  const handleClearAllFilters = () => {
    setFromDate("");
    setToDate("");
    setFromBranchId("");
    setToBranchId("");
    setTableSearch("");
  };

  const toggleShipmentSelection = (shipmentId) => {
    setSelectedShipmentIds((prev) => {
      const next = new Set(prev);

      if (next.has(shipmentId)) {
        next.delete(shipmentId);
      } else {
        next.add(shipmentId);
      }

      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const ids = payableShipments.map((s) => s.shipment_id);

    const allSelected =
      ids.length > 0 && ids.every((id) => selectedShipmentIds.has(id));

    if (allSelected) {
      setSelectedShipmentIds(new Set());
    } else {
      setSelectedShipmentIds(new Set(ids));
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);

      await shipmentClientService.exportClientShipments(
        clientId,
        filterParams(),
      );

      Alert.success("Excel file downloaded successfully!");
    } catch (err) {
      Alert.error("Failed to export Excel file");

      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadStatement = async () => {
    try {
      setDownloading(true);

      await shipmentClientService.downloadClientInvoice(
        clientId,
        filterParams(),
      );

      Alert.success("Statement downloaded successfully!");
    } catch (err) {
      Alert.error("Failed to download statement");

      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const handlePaymentAmountChange = (e) => {
    const filtered = inputFilters.amount(e.target.value);

    const wasOver = Number(paymentAmount) > maxPaymentAmount;

    setPaymentAmount(filtered);

    const error = validateField("paymentAmount", filtered, paymentAmountRule);

    if (error && Number(filtered) > maxPaymentAmount && !wasOver) {
      Alert.error(
        `Payment amount cannot exceed due amount of ${fullCurrency(maxPaymentAmount)}`,
      );
    }
  };

  const handleBatchPayment = async (e) => {
    e.preventDefault();

    if (selectedPayable.length === 0) {
      Alert.error(
        "Select at least one shipment with balance to process payment",
      );

      return;
    }

    if (!validate({ paymentAmount }, validationRules)) return;

    if (parseFloat(paymentAmount) > maxPaymentAmount) {
      Alert.error(
        `Payment amount cannot exceed due amount of ${fullCurrency(maxPaymentAmount)}`,
      );

      return;
    }

    const confirmed = await Alert.confirm(
      `Process payment of ${fullCurrency(paymentAmount)} for ${selectedPayable.length} selected shipment(s)?`,

      "Confirm Payment",
    );

    if (!confirmed) return;

    try {
      setSubmitting(true);

      const response = await shipmentClientService.batchPayment(clientId, {
        payment_amount: parseFloat(paymentAmount),

        payment_mode: paymentMode,

        shipment_ids: selectedPayable.map((s) => s.shipment_id),

        ...filterParams(),
      });

      if (response.success) {
        Alert.success(response.message);

        setPaymentAmount("");

        setPaymentMode("cash");

        if (response.client) {
          setClient(response.client);
        }

        if (response.updated_shipment_details?.length) {
          mergeUpdatedShipments(response.updated_shipment_details);
        } else {
          await fetchClientShipments({ silent: true });
        }
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || "Failed to process payment");

      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const getPaymentStatusBadge = (mode) => {
    const statusColors = {
      to_pay: "bg-yellow-100 text-yellow-800",

      paid: "bg-green-100 text-green-800",

      paid_balance: "bg-blue-100 text-blue-800",

      credit: "bg-orange-100 text-orange-800",
    };

    return statusColors[mode] || "bg-gray-100 text-gray-800";
  };

  const formatPaymentMode = (mode) => {
    if (!mode) return "—";

    return mode
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const hubLabel = (hub) => {
    if (!hub) return "";

    const code = hub.location_code ? ` (${hub.location_code})` : "";

    return `${hub.location || hub.name || "Hub"}${code}`;
  };

  const hubOptionFromShipment = useCallback((shipment, side) => {
    const isSender = side === "sender";
    const id = isSender
      ? shipment.sender_location_hub_id
      : shipment.receiver_location_hub_id;
    if (!id) return null;
    const embedded = isSender
      ? shipment.sender_location_hub
      : shipment.receiver_location_hub;
    if (embedded?.id) {
      return {
        id: embedded.id,
        location: embedded.location || embedded.name,
        location_code: embedded.location_code,
      };
    }
    return {
      id,
      location: isSender ? shipment.sender_location : shipment.receiver_location,
      location_code: "",
    };
  }, []);

  const collectBookedHubOptions = useCallback(
    (side, onlyFromBranchId = null) => {
      const map = new Map();
      bookedShipmentsForRoutes.forEach((shipment) => {
        if (
          onlyFromBranchId &&
          String(shipment.sender_location_hub_id) !== String(onlyFromBranchId)
        ) {
          return;
        }
        const hub = hubOptionFromShipment(shipment, side);
        if (hub?.id) {
          map.set(String(hub.id), hub);
        }
      });
      return Array.from(map.values()).sort((a, b) =>
        String(a.location || "").localeCompare(String(b.location || "")),
      );
    },
    [bookedShipmentsForRoutes, hubOptionFromShipment],
  );

  const bookedFromHubOptions = useMemo(
    () => collectBookedHubOptions("sender"),
    [collectBookedHubOptions],
  );

  const bookedToHubOptions = useMemo(
    () => collectBookedHubOptions("receiver", fromBranchId || null),
    [collectBookedHubOptions, fromBranchId],
  );

  useEffect(() => {
    if (
      fromBranchId &&
      !bookedFromHubOptions.some((h) => String(h.id) === String(fromBranchId))
    ) {
      setFromBranchId("");
    }
  }, [fromBranchId, bookedFromHubOptions]);

  useEffect(() => {
    if (
      toBranchId &&
      !bookedToHubOptions.some((h) => String(h.id) === String(toBranchId))
    ) {
      setToBranchId("");
    }
  }, [toBranchId, bookedToHubOptions]);

  const allVisibleSelected =
    payableShipments.length > 0 &&
    payableShipments.every((s) => selectedShipmentIds.has(s.shipment_id));

  const clientSubtitle = client
    ? [client.full_name, client.company_name].filter(Boolean).join(" · ")
    : "";

  const showPaymentForm =
    listSummary.balance_amount > 0 && payableShipments.length > 0;

  return (
    <DashboardLayout>
      <div className={listPageWrapperClass}>
        <ListPageHeader
          title={t("pages.shipmentClient.batchBill")}

          subtitle={clientSubtitle}

          icon={IndianRupee}

          backTo="/shipment-clients"

          titleClassName="text-secondary"

          iconClassName="w-5 h-5 text-secondary shrink-0"

          className="mb-3"
        >
          <Button
            variant="secondary"

            icon={FileText}

            mobileIconOnly

            onClick={handleDownloadStatement}

            disabled={downloading}

            loading={downloading}

            title="Download Statement"
          >
            {downloading ? "Downloading..." : "Download Statement"}
          </Button>

          <Button
            variant="success"

            icon={Download}

            mobileIconOnly

            onClick={handleExportExcel}

            disabled={exporting || shipments.length === 0}

            loading={exporting}

            title="Export Excel"
          >
            {exporting ? "Exporting..." : "Export Excel"}
          </Button>

          <Button
            variant="secondary"
            className="hidden md:inline-flex"
            onClick={() => navigate("/shipment-clients")}
          >
            Back
          </Button>
        </ListPageHeader>

        <div className="bg-white rounded-xl shadow-sm p-2.5 sm:p-3 mb-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:items-end">
            <div className="min-w-0">
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Date From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={compactControlClass}
              />
            </div>
            <div className="min-w-0">
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Date To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={compactControlClass}
              />
            </div>
            <div className="min-w-0">
              <label className="block text-[11px] font-medium text-gray-500 mb-1">From branch</label>
              <SelectField
                variant="form"
                value={fromBranchId}
                onChange={(e) => {
                  setFromBranchId(e.target.value);
                  setToBranchId("");
                }}
                className="w-full"
                triggerClassName={compactControlClass}
              >
                <option value="">All branches</option>
                {bookedFromHubOptions.map((hub) => (
                  <option key={hub.id} value={String(hub.id)}>
                    {hubLabel(hub)}
                  </option>
                ))}
              </SelectField>
            </div>
            <div className="min-w-0">
              <label className="block text-[11px] font-medium text-gray-500 mb-1">To branch</label>
              <SelectField
                variant="form"
                value={toBranchId}
                onChange={(e) => setToBranchId(e.target.value)}
                className="w-full"
                triggerClassName={compactControlClass}
              >
                <option value="">
                  {fromBranchId ? "All destinations for this origin" : "All branches"}
                </option>
                {bookedToHubOptions.map((hub) => (
                  <option key={hub.id} value={String(hub.id)}>
                    {hubLabel(hub)}
                  </option>
                ))}
              </SelectField>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
          <StatTile label="Filtered total" value={fullCurrency(listSummary.total_amount)} tone="blue" />
          <StatTile label="Paid amount" value={fullCurrency(listSummary.paid_amount)} tone="green" />
          <StatTile label="Due amount" value={fullCurrency(listSummary.balance_amount)} tone="red" />
          <StatTile label="Filtered shipments" value={listSummary.total_shipments} tone="purple" />
        </div>

        {showPaymentForm && (
          <form
            onSubmit={handleBatchPayment}
            className="bg-white rounded-xl shadow-sm p-2.5 sm:p-3 mb-3"
          >
            <p className="text-xs mb-2">
              {selectedPayable.length > 0 ? (
                <span className="text-cyan-700 font-medium">
                  Selected due: {fullCurrency(selectedDueAmount)}
                </span>
              ) : (
                <span className="text-gray-500">
                  Select shipments in the list below, then enter payment amount.
                </span>
              )}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 md:gap-3 md:items-end">
              <div className="min-w-0">
                <label className="block text-[11px] font-medium text-gray-500 mb-1">
                  Amount (₹) <span className="text-red-500">*</span>
                  {selectedPayable.length > 0 && (
                    <span className="text-gray-400 font-normal">
                      {" "}
                      · max {fullCurrency(maxPaymentAmount)}
                    </span>
                  )}
                </label>

                <input
                  type="text"

                  value={paymentAmount}

                  onChange={handlePaymentAmountChange}

                  placeholder="0.00"

                  className={`${compactControlClass} ${errors.paymentAmount ? "border-red-400" : ""}`}

                  required
                />

                <ValidationMessage error={errors.paymentAmount} />
              </div>

              <div className="min-w-0">
                <label className="block text-[11px] font-medium text-gray-500 mb-1">
                  Mode <span className="text-red-500">*</span>
                </label>

                <SelectField
                  variant="form"

                  value={paymentMode}

                  onChange={(e) => setPaymentMode(e.target.value)}

                  className="w-full"

                  triggerClassName={compactControlClass}

                  required
                >
                  <option value="cash">Cash</option>

                  <option value="upi">UPI</option>

                  <option value="bank_transfer">Bank Transfer</option>

                  <option value="cheque">Cheque</option>

                  <option value="online">Online</option>
                </SelectField>
              </div>

              <div className="col-span-2 md:col-span-1">
                <Button
                  variant="success"

                  type="submit"

                  icon={IndianRupee}

                  loading={submitting}

                  disabled={submitting || !isPaymentReady}

                  className="w-full md:w-auto h-10 !px-4"
                >
                  {submitting ? "Processing..." : "Process Payment"}
                </Button>
              </div>
            </div>
          </form>
        )}

        <div className="bg-white rounded-xl shadow-sm relative">
          <div className="px-3 py-2 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                Shipment Details ({listSummary.total_shipments} in list)
              </h2>
              {listRefreshing && !initialLoading && (
                <span className="text-[10px] text-gray-400">Updating…</span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-auto">
              <input
                type="text"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                placeholder="Search ID, sender…"
                className={tableSearchInputClass}
              />
              {(hasPageFilters || hasTableSearch) && (
                <button
                  type="button"
                  onClick={handleClearAllFilters}
                  className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 hover:underline whitespace-nowrap"
                >
                  <X className="w-3 h-3" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {initialLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader
                fullScreen={false}
                size="default"
                message="Loading shipments..."
              />
            </div>
          ) : (
            <>
              <div className="hidden lg:block overflow-x-auto">
                <table className={`${tableClass} min-w-full`}>
                  <thead>
                    <tr className={tableHeadRowClass}>
                      <th className={`${tableHeadCellClass} w-10`}>
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleSelectAllVisible}
                          disabled={payableShipments.length === 0}
                          title="Select all visible with balance"
                          className="rounded border-gray-300"
                        />
                      </th>
                      <th className={tableHeadCellClass}>Shipment ID</th>
                      <th className={tableHeadCellClass}>Date</th>
                      <th className={tableHeadCellClass}>From</th>
                      <th className={tableHeadCellClass}>To</th>
                      <th className={tableHeadCellClass}>Sender</th>
                      <th className={tableHeadCellClass}>Receiver</th>
                      <th className={tableHeadCellClass}>Status</th>
                      <th className={`${tableHeadCellClass} text-right`}>Total</th>
                      <th className={`${tableHeadCellClass} text-right`}>Paid</th>
                      <th className={`${tableHeadCellClass} text-right`}>Balance</th>
                      <th className={tableHeadCellClass}>Payment</th>
                    </tr>
                  </thead>

                  <tbody className={tableBodyClass}>
                    {listShipments.length === 0 && (
                      <tr>
                        <td
                          colSpan={12}
                          className={`${tableCellClass} text-center text-sm text-gray-500 py-8`}
                        >
                          No shipments match the current filters
                        </td>
                      </tr>
                    )}
                    {listShipments.map((shipment) => {
                      const hasBalance =
                        Number(shipment.balance_amount || 0) > 0;

                      const checked = selectedShipmentIds.has(
                        shipment.shipment_id,
                      );

                      return (
                        <tr
                          key={shipment.shipment_id}
                          className={tableRowClass}
                        >
                          <td className={tableCellClass}>
                            <input
                              type="checkbox"

                              checked={checked}

                              disabled={!hasBalance}

                              onChange={() =>
                                toggleShipmentSelection(shipment.shipment_id)
                              }

                              className="rounded border-gray-300"
                            />
                          </td>

                          <td className={tableCellClass}>
                            <Button
                              variant="link"

                              onClick={() =>
                                navigate(
                                  `/shipments/view/${shipment.shipment_id}`,
                                )
                              }

                              className={listIdLinkClass}
                            >
                              {shipment.shipment_id}
                            </Button>
                          </td>

                          <td
                            className={`${tableCellClass} text-sm text-gray-700`}
                          >
                            {formatIndianDate(shipment.created_at)}
                          </td>

                          <td
                            className={`${tableCellClass} text-sm text-gray-700`}
                          >
                            {shipment.sender_location || "—"}
                          </td>

                          <td
                            className={`${tableCellClass} text-sm text-gray-700`}
                          >
                            {shipment.receiver_location || "—"}
                          </td>

                          <td
                            className={`${tableCellClass} text-sm text-gray-700`}
                          >
                            {shipment.sender_name}
                          </td>

                          <td
                            className={`${tableCellClass} text-sm text-gray-700`}
                          >
                            {shipment.receiver_name}
                          </td>

                          <td className={tableCellClass}>
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {shipment.status}
                            </span>
                          </td>

                          <td
                            className={`${tableCellClass} text-sm text-right font-medium tabular-nums`}
                          >
                            {fullCurrency(shipment.total_amount)}
                          </td>

                          <td
                            className={`${tableCellClass} text-sm text-right text-green-600 font-medium tabular-nums`}
                          >
                            {fullCurrency(shipment.paid_amount)}
                          </td>

                          <td
                            className={`${tableCellClass} text-sm text-right text-red-600 font-medium tabular-nums`}
                          >
                            {fullCurrency(shipment.balance_amount)}
                          </td>

                          <td className={tableCellClass}>
                            <span
                              className={`px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusBadge(shipment.mode_of_payment)}`}
                            >
                              {formatPaymentMode(shipment.mode_of_payment)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="lg:hidden p-2 space-y-2">
                <div className="flex items-center justify-between gap-2 pb-1 border-b border-gray-100">
                  <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      disabled={payableShipments.length === 0}
                      className="rounded border-gray-300"
                    />
                    Select all with balance
                  </label>
                </div>

                {listShipments.length === 0 && (
                  <div
                    className={`${emptyStateClass} shadow-none py-6 text-sm`}
                  >
                    No shipments match the current filters
                  </div>
                )}

                {listShipments.map((shipment) => {
                  const hasBalance = Number(shipment.balance_amount || 0) > 0;

                  const checked = selectedShipmentIds.has(shipment.shipment_id);

                  return (
                    <div
                      key={shipment.shipment_id}
                      className={`${mobileCardClass} p-3`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <input
                            type="checkbox"

                            checked={checked}

                            disabled={!hasBalance}

                            onChange={() =>
                              toggleShipmentSelection(shipment.shipment_id)
                            }

                            className="rounded border-gray-300 shrink-0"
                          />

                          <button
                            type="button"

                            onClick={() =>
                              navigate(
                                `/shipments/view/${shipment.shipment_id}`,
                              )
                            }

                            className={listIdLinkMobileClass}
                          >
                            {shipment.shipment_id}
                          </button>
                        </div>

                        <span
                          className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${getPaymentStatusBadge(shipment.mode_of_payment)}`}
                        >
                          {formatPaymentMode(shipment.mode_of_payment)}
                        </span>
                      </div>

                      <p className="text-[11px] text-gray-500 mb-1.5">
                        {formatIndianDate(shipment.created_at)} ·{" "}
                        {shipment.status}
                      </p>

                      <p className="text-xs text-gray-600 mb-1">
                        {shipment.sender_location || "—"} →{" "}
                        {shipment.receiver_location || "—"}
                      </p>

                      <p className="text-xs text-gray-600 truncate mb-2">
                        {shipment.sender_name} → {shipment.receiver_name}
                      </p>

                      <div className="grid grid-cols-3 gap-1.5">
                        <div className="rounded-xl bg-blue-50 px-2 py-1.5 min-w-0">
                          <p className="text-[10px] text-blue-700/80">Total</p>

                          <p className="text-xs font-semibold text-blue-700 tabular-nums truncate">
                            {fullCurrency(shipment.total_amount)}
                          </p>
                        </div>

                        <div className="rounded-xl bg-green-50 px-2 py-1.5 min-w-0">
                          <p className="text-[10px] text-green-700/80">Paid</p>

                          <p className="text-xs font-semibold text-green-700 tabular-nums truncate">
                            {fullCurrency(shipment.paid_amount)}
                          </p>
                        </div>

                        <div className="rounded-xl bg-red-50 px-2 py-1.5 min-w-0">
                          <p className="text-[10px] text-red-700/80">Due</p>

                          <p className="text-xs font-bold text-red-600 tabular-nums truncate">
                            {fullCurrency(shipment.balance_amount)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VendorBatchShipmentBill;
